import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import styled from 'styled-components';

import classNames from 'classnames';
import { getComponentMargin, getAnchorId } from '@/utils/builderUtils';
import { moreDescriptiveAriaLabel } from '@/utils/utils';
import beTwMerge from '@/utils/beTwMerge';

import { Action } from '@/components/SingleImageTile/SingleImageTile';
import Button from '@/components/homepage/Button/Button';
import DropdownButton from '@/components/homepage/DropdownButton';
import noUiSlider from 'nouislider';
import RichText from '../../RichText/RichText';
import Title from '../../Title/Title';

interface ITitle {
  content: string;
  titleHTMLTag?: 'h1' | 'h2' | 'h3';
  titleSize?: 'extraLarge' | 'large' | 'medium' | 'small';
  titleWeight?: 'light' | 'normal' | 'medium' | 'bold' | 'black';
}

interface IDiamondColorScaleGraphicProps {
  /**
   * Optional large header.
   * - content*: The content of the header
   * - titleHTMLTag: Heading level that will be used to render the optional header. Defaults to 'h2'
   * - titleSize: Select one of the styles 'extraLarge' | 'large' | 'medium' | 'small'.
   * - titleWeight: Optional header weight. Defaults to 'normal'
   */
  optionalLargeHeader?: ITitle;
  /**
   * Turn on to add margin to the bottom of the component
   */
  margin?: boolean;
  /**
   * Give to the component an anchor link id
   */
  anchorLinkID?: string;
  /**
   * To position the content either to the left or to the right
   */
  graphicOnLeft: boolean;
  /**
   *  Rich text content of the Block
   */
  content: string;
  /**
   * To position the image either to the center or to the start
   */
  mediaAlignment: 'start' | 'center';
  /**
   * The list of cta buttons
   * Props.
   * - text*: Text of the button
   * - url*: url to link to
   * - newTab*: If the link should open in a new tab
   */
  actions?: Action[];
}

const Container = styled.div`
  .noUi-target {
    background: #fafafa;
    border-radius: 4px;
    border: 1px solid #d3d3d3;
    box-shadow:
      inset 0 1px 1px #f0f0f0,
      0 3px 6px -5px #bbb;
  }
  .noUi-target,
  .noUi-target * {
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    -webkit-user-select: none;
    touch-action: none;
    -moz-user-select: none;
    user-select: none;
    box-sizing: border-box;
  }
  .noUi-base,
  .noUi-connects {
    width: 100%;
    height: 100%;
    position: relative;
  }
  .noUi-connects {
    border-radius: 3px;
  }
  .noUi-connects {
    overflow: hidden;
    z-index: 0;
  }
  .noUi-connect {
    background: #3fb8af;
  }
  .noUi-connect,
  .noUi-origin {
    will-change: transform;
    position: absolute;
    z-index: 1;
    top: 0;
    right: 0;
    height: 100%;
    width: 100%;
    -ms-transform-origin: 0 0;
    -webkit-transform-origin: 0 0;
    -webkit-transform-style: preserve-3d;
    transform-origin: 0 0;
    transform-style: flat;
    background-color: #183e40;
  }
  .noUi-touch-area {
    height: 100%;
    width: 100%;
  }
  .noUi-pips-horizontal {
    padding: 10px 0;
    height: 80px;
    top: 100%;
    left: 0;
    width: 100%;
  }
  .noUi-pips {
    position: absolute;
    color: #999;
  }
  .noUi-pips,
  .noUi-pips * {
    box-sizing: border-box;
  }
  .noUi-marker-horizontal.noUi-marker {
    margin-left: -1px;
    width: 2px;
    height: 5px;
  }
  .noUi-marker-large {
    background: #aaa;
  }
  .noUi-marker {
    position: absolute;
    background: #ccc;
  }
  .noUi-pips,
  .noUi-pips * {
    box-sizing: border-box;
  }
  .noUi-value {
    position: absolute;
    white-space: nowrap;
    text-align: center;
  }
  .nouislider-custom {
    height: 4px;
    box-shadow: none;
    border: 0;
    background-color: #e1e1e1;
    position: relative;
  }

  .nouislider-custom .noUi-base {
    z-index: inherit;
  }
  .noUi-horizontal .noUi-origin {
    height: 0;
  }
  .noUi-handle::before,
  .noUi-handle::after {
    content: none !important;
    display: none !important;
  }
  .nouislider-custom .noUi-handle {
    background: #fff;
    backface-visibility: hidden;
    position: absolute;
    width: 16px;
    height: 16px;
    right: -8px;
    top: -6px;
    border-radius: 10em;
    background-color: #183e40;
    box-shadow: none;
    border: 0;
    cursor: pointer;
  }
  .nouislider-custom .noUi-pips-horizontal {
    display: flex;
    height: auto;
    color: inherit;
  }
  .nouislider-custom .noUi-pips-horizontal .noUi-marker-horizontal.noUi-marker {
    color: inherit;
    width: 1px;
    height: 4px;
    margin-top: -14px;
    background-color: white;
  }
  .nouislider-custom .noUi-pips-horizontal .noUi-marker-large {
    display: none;
  }
  .nouislider-custom .noUi-value {
    font-family: 'Avenir Book';
    color: inherit;
    font-size: 12px;
    line-height: 1em;
  }
  .nouislider-custom .noUi-value-horizontal {
    transform: translate(-50%, 0%);
  }
`;

const diamondCuts = [
  {
    title: 'M',
    description:
      "Has a gentle, warm tint that's usually visible face-up, making it a natural match for yellow and rose gold settings.",
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-10-M-v3.png?v=1',
    arrowStyle: '10px',
  },
  {
    title: 'L',
    description:
      "Features a subtle hint of warmth, which is difficult to detect especially if it's a brilliant cut or paired with yellow- or rose-toned settings.",
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-9-L-v2.png?v=1',
    arrowStyle: '11.11%',
  },
  {
    title: 'K',
    description: 'Faint color detectable to the naked eye. Offers great value.',
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-8-K.png?v=1',
    arrowStyle: '22.22%',
  },
  {
    title: 'J',
    description: 'Near-colorless. Slightly detected color—a good value.',
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-1-J.png',
    arrowStyle: '33.33%',
  },
  {
    title: 'I',
    description: 'Near-colorless. Slightly detected color—a good value.',
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-2-I.png',
    arrowStyle: '44.44%',
  },
  {
    title: 'H',
    description:
      'Near-colorless. Color noticeable when compared to diamonds of better grades, but offers excellent value.',
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-3-H.png',
    arrowStyle: '55.55%',
  },
  {
    title: 'G',
    description:
      'Near-colorless. Color noticeable when compared to diamonds of better grades, but offers excellent value.',
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-4-G.png',
    arrowStyle: '66.66%',
  },
  {
    title: 'F',
    description:
      'Colorless. Slight color detected by an expert gemologist, but still considered a “colorless” grade—a high-quality diamond.',
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-5-F.png',
    arrowStyle: '77.77%',
  },
  {
    title: 'E',
    description:
      'Colorless. Only miniscule traces of color can be detected by an expert gemologist—a rare, high quality diamond.',
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-6-E.png',
    arrowStyle: '88.88%',
  },
  {
    title: 'D',
    description: 'Absolutely colorless or icy white. The highest color grade—extremely rare and most expensive.',
    imageURL: 'https://css.brilliantearth.com/static/img/diamond-search/diamond-color/color-scale-7-D.png',
    arrowStyle: '100%',
  },
];

export default function DiamondColorScaleGraphic({
  optionalLargeHeader,
  anchorLinkID,
  margin = false,
  content,
  mediaAlignment,
  actions,
  graphicOnLeft,
}: IDiamondColorScaleGraphicProps) {
  const [activeCut, setActiveCut] = useState<number>(diamondCuts.length - 1);
  const sliderRef = useRef<any>(null);

  const formattedActions = actions?.map(action => ({
    ...action,
    // @ts-ignore
    color: action.color === 'dark' ? 'dark-green' : 'white',
  }));
  const updateSliderUI = valueIndex => {
    setActiveCut(valueIndex);
  };
  useEffect(() => {
    const colorSlider = sliderRef.current;
    if (colorSlider && !colorSlider.noUiSlider) {
      const formatModalColorSlider = {
        to: value => diamondCuts[value].title,
        from: value => {
          const index = diamondCuts.findIndex(cut => cut.title === value);
          return index !== -1 ? index : 0;
        },
      };
      noUiSlider.create(colorSlider, {
        start: diamondCuts.length - 1,
        range: {
          min: 0,
          max: diamondCuts.length - 1,
        },
        step: 1,
        connect: [true, true],
        pips: { mode: 'steps', format: formatModalColorSlider, density: 100 } as any,
      });
      colorSlider.noUiSlider.on('change', value => {
        updateSliderUI(Number(value[0]));
      });
      return () => {
        if (colorSlider.noUiSlider) {
          colorSlider.noUiSlider.off('change');
          colorSlider.noUiSlider.destroy();
        }
      };
    }
  }, [diamondCuts]);

  return (
    <div
      {...getAnchorId(anchorLinkID)}
      className={classNames('tw-w-[85%] tablet:tw-w-[85%] tw-max-w-[1440px] tw-mx-auto', getComponentMargin(margin))}
    >
      {optionalLargeHeader?.content && (
        <Title
          component={optionalLargeHeader?.titleHTMLTag || 'h2'}
          weight={optionalLargeHeader?.titleWeight}
          size={optionalLargeHeader?.titleSize}
          alignment="center"
        >
          {optionalLargeHeader?.content}
        </Title>
      )}
      <div
        className={beTwMerge(
          'tw-flex tw-items-center lg:tw-mx-auto tw-flex-col lg:tw-flex-row tw-w-full lg:tw-w-full tw-gap-x-[64px] tw-max-w-[1440px]',
          !graphicOnLeft ? 'lg:tw-flex-row-reverse' : '',
          mediaAlignment === 'start' ? 'lg:tw-items-start' : ''
        )}
      >
        {/* Graphic */}
        <div className={beTwMerge('tw-w-full lg:tw-w-1/2 tw-mb-4 lg:tw-mb-0')}>
          <Container>
            <div className="modal-slider-tips diamond-cut-slider">
              <div className="search-noUiSlider-wrap tw-w-full">
                <div className="clarity-scale tw-mb-10 tw-aspect-[194/122] tw-max-w-[194px] tw-relative img-responsive center-block">
                  <Image fill src={diamondCuts[activeCut].imageURL} alt="" className="" />
                </div>

                <div>
                  <div className="tw-h-[30px]">
                    <div
                      id="modal-color-slider"
                      ref={sliderRef}
                      className="nouislider-custom relative tw-h-[4px] tw-b-0 tw-bg-[#e1e1e1]"
                    />
                  </div>
                  <div className="tw-min-h-[90px]">
                    <div className="tw-block tw-relative tw-p-[10px_0_0] tw-mt-[5px] tw-z-[1030]" role="tooltip">
                      <div
                        className="tw-absolute tw-w-0 tw-h-0 tw-border-solid tw-border-transparent tw-border-style-solid tw-border-b-[#183e40] tw-border-x-[10px] tw-border-b-[10px] tw-top-[-1px]"
                        style={{
                          left: diamondCuts[activeCut].arrowStyle,
                          marginLeft: diamondCuts[activeCut].arrowStyle === '100%' ? '-20px' : '-10px',
                        }}
                      />
                      <div className="tw-max-w-[100%] tw-text-center tw-bg-[#183e40] tw-p-[1em_2em] tw-text-white">
                        <h3 className="tw-text-[16px]">{diamondCuts[activeCut].title} Color</h3>
                        <p className="tw-text-[14px] tw-font-[350]">{diamondCuts[activeCut].description}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>

        {/* Copy */}
        <div className={beTwMerge('tw-w-full lg:tw-w-1/2 tw-max-w-[1172px] lg:tw-mt-0 lg:tw-mb-0 tw-mb-4')}>
          <RichText content={content} />

          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 md:tw-flex-row tw-gap-2 md:tw-gap-4 tw-w-full tw-mt-12">
            {formattedActions?.slice(0, 4).map((action, i) => {
              const { label, color, type } = action;

              if (action.actions) {
                const { actions: subActions } = action;
                return (
                  <DropdownButton
                    key={i}
                    label={label}
                    options={subActions}
                    // @ts-ignore
                    color={color}
                    type={type}
                    className={beTwMerge(
                      `tw-basis-[calc(50%-8px)] tw-w-full tw-text-center tw-max-w-none [&>button]:tw-w-full [&>button]:tw-h-full [&>button]:tw-leading-4 [&>button]:tw-py-3 [&>button>span]:tw-max-w-[calc(100%-28px)] [&>button>span]:tw-overflow-hidden tw-h-[44px] tw-whitespace-nowrap [&>button>span]:tw-text-ellipsis`
                    )}
                  />
                );
              }
              const { onClick, href } = action;
              return (
                <Button
                  className={beTwMerge(
                    `tw-basis-[calc(50%-8px)] tw-max-w-none tw-w-full tw-text-center tw-whitespace-nowrap tw-text-ellipsis md:tw-overflow-hidden`
                  )}
                  ariaLabel={moreDescriptiveAriaLabel(optionalLargeHeader?.content, label)}
                  key={i}
                  label={label}
                  // @ts-ignore
                  color={color}
                  type={type}
                  href={href}
                  onClick={onClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
